import {useRef} from 'react'
import {useMergeState} from '@eitje/hooks'
import utils from '@eitje/utils'
import _ from 'lodash'
// LegacyModal is the compatibility boundary around AntD Modal.
// eslint-disable-next-line no-restricted-imports
import {Modal} from 'antd'
import {goBack} from '@eitje/bridge'
import {Layout} from '../layout'
import {formatCssVars, makeCnVariants, requireProps} from '../../helpers'
import {SpinnerWithBackdrop} from '../spinner'
import {useFitInViewportCheck} from '../../hooks'
import {t} from '@eitje/bridge'
import {ModalFooter} from './footer'
import {ModalHeader} from './header'
import {LegacyModalProvider} from './modal_context'
import './styles/body.less'
import './styles/index.less'
import {useHydrateModalRouteFromLocalStorage} from '../modal/hooks/use_hydrate_modal_route_from_local_storage'

const MODAL_LEFT_PANEL_WIDTH = 200

const ModalInner = ({
	ref,
	name,
	width = 560,
	loading,
	hideHeader,
	hideFooter,
	height,
	className,
	onCancel,
	title,
	subtitle,
	LeftHeader,
	RightHeader,
	children,
	LeftPanel,
	leftPanelProps,
	bodyMaxHeight,
	leftFooterContent,
	hideCancel,
	form,
	onSubmit,
	afterDel,
	submitIsDanger,
	SubmitButtonComponent,
	FooterSubmitButton,
	buttons,
	insertAtIndex,
	confirmSubmit,
	submitConfirmation,
	hideSubmit,
	onDel,
	docsId,
	okButtonProps,
}) => {
	const {vertical} = useFitInViewportCheck({element: ref, offset: 100})

	const modalClassName = name.replace(/_/g, '-').concat('-modal')
	const classNames = utils.makeCns(
		'eitje-modal-2',
		'hide-close',
		!vertical && 'small-top-offset',
		height && 'set-fixed-height',
		modalClassName,
		className,
	)

	return (
		<Modal
			width={width}
			footer={null}
			open
			className={classNames}
			onCancel={onCancel}
			transitionName="" // this is needed to disable any animation on the antd modal
			maskTransitionName=""
			focusable={{trap: false}}
		>
			<div
				className="eitje-modal-2-main"
				ref={ref}
				style={formatCssVars({
					'modal-height': height,
				})}
			>
				{!hideHeader && <ModalHeader title={title} subtitle={subtitle} LeftHeader={LeftHeader} RightHeader={RightHeader} />}
				<Layout relative block>
					<SpinnerWithBackdrop isSpinning={!!loading} isNested />

					<ModalBody LeftPanel={LeftPanel} leftPanelProps={leftPanelProps} bodyMaxHeight={bodyMaxHeight} hideFooter={hideFooter}>
						{children}
					</ModalBody>
					{!hideFooter && (
						<ModalFooter
							leftFooterContent={leftFooterContent}
							hideCancel={hideCancel}
							form={form}
							onSubmit={onSubmit}
							afterDel={afterDel}
							submitIsDanger={submitIsDanger}
							SubmitButtonComponent={SubmitButtonComponent}
							FooterSubmitButton={FooterSubmitButton}
							buttons={buttons}
							insertAtIndex={insertAtIndex}
							confirmSubmit={confirmSubmit}
							submitConfirmation={submitConfirmation}
							hideSubmit={hideSubmit}
							onDel={onDel}
							docsId={docsId}
							okButtonProps={okButtonProps}
						/>
					)}
				</Layout>
			</div>
		</Modal>
	)
}

const ModalBody = ({children, LeftPanel, leftPanelProps, bodyMaxHeight, hideFooter}) => {
	const classNames = makeCnVariants('eitje-modal-2-body', hideFooter && 'without-footer', LeftPanel && 'has-left-panel')
	const maxHeight = bodyMaxHeight ? `${bodyMaxHeight}px` : 'auto'
	return (
		<div className={classNames} style={{maxHeight}}>
			{LeftPanel && <ModalLeftPanel LeftPanel={LeftPanel} leftPanelProps={leftPanelProps} />}
			{children}
		</div>
	)
}

const ModalLeftPanel = ({LeftPanel, leftPanelProps}) => {
	return (
		<Layout className="eitje-modal-2-left-panel" width={MODAL_LEFT_PANEL_WIDTH} style={{maxWidth: MODAL_LEFT_PANEL_WIDTH}}>
			<LeftPanel {...leftPanelProps} />
		</Layout>
	)
}

/** @deprecated Use Modal from @eitje/web_components for new code. */
export const LegacyModal = ({
	contextProps,
	name,
	i18nOpts,
	onCancel,
	// Props for ModalInner that are just forwarded
	...otherInnerProps
}) => {
	const modalRef = useRef()
	const [modalContextProps, setModalProps, setRaw] = useMergeState()

	const {isHydrating: isHydratingModalRouteMetadata} = useHydrateModalRouteFromLocalStorage()
	const singleName = utils.alwaysDefinedArray(name)[0] // form can have multiple names, modal (for now) can't

	const handleCancel = onCancel || goBack

	const resetModalProps = () => setRaw({})

	requireProps('LegacyModal', {name})

	const modalT = (name, rest) => {
		let keys = [`modals.${singleName}.${name}`]
		if (_.isString(rest)) keys = [...keys, rest]
		return t(keys, {...i18nOpts, ...rest})
	}
	if (isHydratingModalRouteMetadata) return null

	return (
		<LegacyModalProvider
			value={{
				modalRef,
				setModalProps,
				setModalPropsRaw: setRaw,
				resetModalProps,
				goBack,
				onCancel: handleCancel,
				t: modalT,
				namespace: `modals.${singleName}`,
				names: name,
				name: singleName,
				...contextProps,
			}}
		>
			<ModalInner
				// Props for ModalInner that are just forwarded
				{...otherInnerProps}
				name={singleName}
				onCancel={handleCancel}
				{...modalContextProps}
				ref={modalRef}
			/>
		</LegacyModalProvider>
	)
}
